<template>

  <div id="layout-wrapper">
    <NavigationBar />

    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <div>
            <!-- Page header -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Announcements</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                      <li class="breadcrumb-item active">Company Communications</li>
                      <li class="breadcrumb-item active">Announcements</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filters and controls section -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-wrap align-items-center mb-3">
                      <h5 class="card-title me-3 mb-3 mb-md-0">Announcement Filters</h5>
                      <div class="ms-auto">
                        <button @click="createAnnouncement" class="btn btn-primary me-2">
                          <i class="mdi mdi-plus me-1"></i> Create Announcement
                        </button>
                        <button @click="refreshData" class="btn btn-secondary me-2">
                          <i class="mdi mdi-refresh me-1"></i> Refresh
                        </button>
                        <button @click="exportAnnouncements" class="btn btn-success">
                          <i class="mdi mdi-file-export me-1"></i> Export
                        </button>
                      </div>
                    </div>

                    <div class="row g-3">
                      <div class="col-md-3">
                        <label class="form-label">Announcement Type</label>
                        <select v-model="filters.type" class="form-select">
                          <option value="">All Types</option>
                          <option v-for="type in announcementTypes" :key="type" :value="type">
                            {{ type }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Priority</label>
                        <select v-model="filters.priority" class="form-select">
                          <option value="">All Priorities</option>
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Status</label>
                        <select v-model="filters.status" class="form-select">
                          <option value="">All Statuses</option>
                          <option value="Active">Active</option>
                          <option value="Draft">Draft</option>
                          <option value="Archived">Archived</option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Department</label>
                        <select v-model="filters.departmentId" class="form-select">
                          <option value="">All Departments</option>
                          <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                            {{ dept.departmentName }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-6">
                        <label class="form-label">Date Range</label>
                        <div class="input-group">
                          <input v-model="filters.startDate" type="date" class="form-control">
                          <span class="input-group-text">to</span>
                          <input v-model="filters.endDate" type="date" class="form-control">
                        </div>
                      </div>

                      <div class="col-md-6">
                        <label class="form-label">Search</label>
                        <div class="position-relative">
                          <input
                              type="text"
                              v-model="filters.search"
                              class="form-control"
                              placeholder="Search by title, content..."
                          >
                          <i class="bx bx-search-alt search-icon position-absolute top-50 end-0 translate-middle-y me-2"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Statistics cards -->
            <div class="row">
              <!-- Total Announcements -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-primary h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-primary rounded-circle text-white font-size-20">
                            <i class="mdi mdi-bullhorn font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Total Announcements</h6>
                        <h4 class="mb-0">{{ metrics.totalAnnouncements || 0 }}</h4>
                        <span class="text-muted font-size-12">
                          All time
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Active Announcements -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-success h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-success rounded-circle text-white font-size-20">
                            <i class="mdi mdi-bullhorn-outline font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Active Announcements</h6>
                        <h4 class="mb-0">{{ metrics.activeAnnouncements || 0 }}</h4>
                        <span class="text-success font-size-12">Currently published</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- High Priority -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-danger h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-danger rounded-circle text-white font-size-20">
                            <i class="mdi mdi-alert-circle font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">High Priority</h6>
                        <h4 class="mb-0">{{ metrics.highPriority || 0 }}</h4>
                        <span class="text-danger font-size-12">Important announcements</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Require Acknowledgment -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-info h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-info rounded-circle text-white font-size-20">
                            <i class="mdi mdi-checkbox-marked-circle-outline font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Require Acknowledgment</h6>
                        <h4 class="mb-0">{{ metrics.requireAcknowledgment || 0 }}</h4>
                        <span class="text-info font-size-12">Employee confirmation needed</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Announcement Type Breakdown and Recent Announcements -->
            <div class="row">
              <!-- Announcement Type Breakdown -->
              <div class="col-xl-6 col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Announcements by Type</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="metrics.typeCounts && Object.keys(metrics.typeCounts).length > 0" class="chart-container" style="position: relative; height:250px; width:100%">
                      <div class="table-responsive">
                        <table class="table table-sm table-bordered">
                          <thead class="table-light">
                          <tr>
                            <th>Announcement Type</th>
                            <th class="text-center">Count</th>
                            <th class="text-center">Percentage</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(count, type) in metrics.typeCounts" :key="type">
                            <td>{{ type }}</td>
                            <td class="text-center">{{ count }}</td>
                            <td class="text-center">
                              <div class="progress" style="height: 6px;">
                                <div
                                    class="progress-bar"
                                    :class="getAnnouncementTypeColor(type)"
                                    role="progressbar"
                                    :style="`width: ${getAnnouncementTypePercentage(type)}%`"
                                    :aria-valuenow="getAnnouncementTypePercentage(type)"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                              </div>
                              {{ getAnnouncementTypePercentage(type) }}%
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div v-else class="text-center p-4">
                      <i class="mdi mdi-chart-bar font-size-48 text-muted"></i>
                      <p class="mt-2">No announcement type data available</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Recent Announcements List -->
              <div class="col-xl-6 col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Recent Announcements</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="recentAnnouncements.length > 0" class="recent-announcements-list">
                      <div v-for="announcement in recentAnnouncements" :key="announcement.id" class="recent-announcement-item mb-3">
                        <div class="d-flex align-items-center p-3 border rounded">
                          <div class="announcement-date-badge me-3 text-center">
                            <span class="date-month">{{ formatDateMonth(announcement.publish_date) }}</span>
                            <span class="date-day fs-4 fw-bold d-block">{{ formatDateDay(announcement.publish_date) }}</span>
                          </div>
                          <div class="announcement-details flex-grow-1">
                            <h6 class="announcement-title mb-1">{{ announcement.title }}</h6>
                            <div class="announcement-meta text-muted small">
                              <span><i class="mdi mdi-bullhorn-outline me-1"></i>{{ announcement.announcement_type }}</span>
                              <span class="ms-2"><i class="mdi mdi-eye me-1"></i>{{ announcement.views || 0 }} views</span>
                            </div>
                          </div>
                          <div class="announcement-type-badge ms-2">
                            <span class="badge rounded-pill" :class="getPriorityBadgeClass(announcement.priority)">{{ announcement.priority }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center p-4">
                      <i class="mdi mdi-bullhorn font-size-48 text-muted"></i>
                      <p class="mt-2">No recent announcements published</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Announcements List Table -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title">Announcements List</h5>
                  </div>
                  <div class="card-body">
                    <!-- Loading overlay -->
                    <div v-if="loading" class="text-center p-5">
                      <div class="spinner-border text-primary me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2">Loading announcements data...</p>
                    </div>

                    <!-- Empty state -->
                    <div v-else-if="!announcements.length" class="text-center p-5">
                      <i class="mdi mdi-bullhorn-outline font-size-48 text-muted"></i>
                      <h5 class="mt-2">No announcements found</h5>
                      <p class="text-muted">Try adjusting your filters or create a new announcement</p>
                      <button @click="resetFilters" class="btn btn-sm btn-outline-primary mt-2">Reset Filters</button>
                    </div>

                    <!-- Announcements table -->
                    <div v-else class="table-responsive">
                      <table class="table table-hover table-bordered align-middle mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Announcement</th>
                          <th>Details</th>
                          <th>Dates</th>
                          <th>Status</th>
                          <th class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="announcement in announcements" :key="announcement.id">
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="avatar-xs me-2 rounded p-1 text-center" :class="getAnnouncementTypeColor(announcement.announcement_type)">
                                <i class="mdi mdi-bullhorn font-size-18 text-white"></i>
                              </div>
                              <div>
                                <h5 class="font-size-14 mb-0">{{ announcement.title }}</h5>
                                <p class="text-muted mb-0 font-size-12">{{ truncateText(announcement.content, 50) }}</p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div class="d-flex flex-column">
                              <div>
                                <span class="badge bg-light text-dark me-1">Type:</span>
                                <span>{{ announcement.announcement_type }}</span>
                              </div>
                              <div class="mt-1">
                                <span class="badge bg-light text-dark me-1">Priority:</span>
                                <span class="badge" :class="getPriorityBadgeClass(announcement.priority)">{{ announcement.priority }}</span>
                              </div>
                              <div class="mt-1">
                                <span class="badge bg-light text-dark me-1">Views:</span>
                                <span>{{ announcement.views || 0 }}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <span class="d-block"><i class="mdi mdi-calendar-check me-1"></i>Published: {{ formatDate(announcement.publish_date) }}</span>
                              <span class="d-block mt-1" v-if="announcement.expiry_date"><i class="mdi mdi-calendar-remove me-1"></i>Expires: {{ formatDate(announcement.expiry_date) }}</span>
                              <span class="d-block mt-1"><i class="mdi mdi-account me-1"></i>By: {{ announcement.created_by }}</span>
                            </div>
                          </td>
                          <td>
                            <div class="d-flex flex-column">
                              <span class="badge" :class="getStatusBadgeClass(announcement.status)">
                                {{ announcement.status }}
                              </span>
                              <span v-if="announcement.require_acknowledgment" class="mt-1 badge bg-info">
                                Requires Acknowledgment
                              </span>
                            </div>
                          </td>
                          <td class="text-center">
                            <div class="d-flex justify-content-center">
                              <button
                                  @click="viewAnnouncementDetails(announcement)"
                                  class="btn btn-sm btn-info me-2"
                                  title="View Details"
                              >
                                <i class="mdi mdi-eye"></i>
                              </button>
                              <button
                                  v-if="canEditAnnouncement(announcement)"
                                  @click="editAnnouncement(announcement)"
                                  class="btn btn-sm btn-primary me-2"
                                  title="Edit Announcement"
                              >
                                <i class="mdi mdi-pencil"></i>
                              </button>
                              <button
                                  v-if="announcement.status === 'Active' && announcement.require_acknowledgment"
                                  @click="viewAcknowledgmentStats(announcement)"
                                  class="btn btn-sm btn-success me-2"
                                  title="View Acknowledgment Stats"
                              >
                                <i class="mdi mdi-chart-bar"></i>
                              </button>
                              <button
                                  v-if="announcement.status === 'Active'"
                                  @click="archiveAnnouncement(announcement)"
                                  class="btn btn-sm btn-warning"
                                  title="Archive Announcement"
                              >
                                <i class="mdi mdi-archive"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="announcements.length" class="d-flex justify-content-between align-items-center mt-4">
                      <div>
                        Showing <span class="fw-medium">{{ announcements.length }}</span> of
                        <span class="fw-medium">{{ pagination.totalAnnouncements }}</span> announcements
                      </div>
                      <nav v-if="pagination.totalPages > 1">
                        <ul class="pagination pagination-rounded mb-0">
                          <li class="page-item" :class="{ disabled: pagination.currentPage <= 1 }">
                            <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage - 1)">
                              <i class="mdi mdi-chevron-left"></i>
                            </a>
                          </li>
                          <li
                              v-for="page in getPageNumbers()"
                              :key="page"
                              class="page-item"
                              :class="{ active: page === pagination.currentPage, disabled: page === '...' }"
                          >
                            <a v-if="page !== '...'" class="page-link" href="javascript:void(0)" @click="changePage(page)">
                              {{ page }}
                            </a>
                            <span v-else class="page-link">{{ page }}</span>
                          </li>
                          <li class="page-item" :class="{ disabled: pagination.currentPage >= pagination.totalPages }">
                            <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage + 1)">
                              <i class="mdi mdi-chevron-right"></i>
                            </a>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Announcement Create/Edit Modal -->


  <!-- Announcement Details Modal -->
  <div class="modal fade" id="announcementDetailsModal" tabindex="-1" aria-labelledby="announcementDetailsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="announcementDetailsModalLabel">Announcement Details</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedAnnouncement">
            <div class="card mb-4">
              <div class="card-body">
                <div class="row">
                  <div class="col-md-8">
                    <h4 class="font-size-18 mb-2">{{ selectedAnnouncement.title }}</h4>
                    <div class="d-flex align-items-center my-2">
                      <span class="badge me-2" :class="getAnnouncementTypeColor(selectedAnnouncement.announcement_type)">
                        {{ selectedAnnouncement.announcement_type }}
                      </span>
                      <span class="badge me-2" :class="getPriorityBadgeClass(selectedAnnouncement.priority)">
                        {{ selectedAnnouncement.priority }}
                      </span>
                      <span class="badge" :class="getStatusBadgeClass(selectedAnnouncement.status)">
                        {{ selectedAnnouncement.status }}
                      </span>
                    </div>
                    <p class="text-muted mb-0">
                      <i class="mdi mdi-account me-1"></i> Posted by: {{ selectedAnnouncement.created_by }}
                    </p>
                    <p class="text-muted mb-0">
                      <i class="mdi mdi-eye me-1"></i> {{ selectedAnnouncement.views || 0 }} views
                    </p>
                    <p v-if="selectedAnnouncement.require_acknowledgment" class="text-info mt-2">
                      <i class="mdi mdi-checkbox-marked-circle-outline me-1"></i> Requires employee acknowledgment
                    </p>
                  </div>
                  <div class="col-md-4 text-end">
                    <div class="mb-2">
                      <span class="badge bg-light text-dark">Published: {{ formatDate(selectedAnnouncement.publish_date) }}</span>
                    </div>
                    <div v-if="selectedAnnouncement.expiry_date" class="mb-2">
                      <span class="badge bg-light text-dark">Expires: {{ formatDate(selectedAnnouncement.expiry_date) }}</span>
                    </div>
                    <div class="mb-2">
                      <span class="badge bg-light text-dark">Last Updated: {{ formatDateTime(selectedAnnouncement.updated_at || selectedAnnouncement.created_at) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card mb-4">
              <div class="card-header bg-transparent">
                <h5 class="card-title mb-0">Announcement Content</h5>
              </div>
              <div class="card-body">
                <div class="announcement-content">
                  <p v-html="formatAnnouncementContent(selectedAnnouncement.content)"></p>
                </div>
              </div>
            </div>

            <div class="row">
              <!-- Target Audience -->
              <div class="col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Target Audience</h5>
                  </div>
                  <div class="card-body">
                    <p class="mb-2"><strong>Audience:</strong> {{ selectedAnnouncement.target_audience }}</p>

                    <div v-if="selectedAnnouncement.departmentsList && selectedAnnouncement.departmentsList.length > 0">
                      <h6 class="font-size-14 mt-3 mb-2">Departments:</h6>
                      <div class="d-flex flex-wrap gap-2">
                        <span v-for="deptId in selectedAnnouncement.departmentsList" :key="deptId" class="badge bg-light text-dark p-2">
                          {{ getDepartmentName(deptId) }}
                        </span>
                      </div>
                    </div>
                    <div v-else-if="selectedAnnouncement.target_audience === 'Specific Departments'">
                      <p class="text-muted mt-2">No specific departments selected.</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Attachments -->
              <div class="col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Attachments</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="selectedAnnouncement.attachmentsList && selectedAnnouncement.attachmentsList.length > 0">
                      <div class="list-group">
                        <div v-for="(attachment, index) in selectedAnnouncement.attachmentsList" :key="index" class="list-group-item list-group-item-action d-flex align-items-center">
                          <i class="mdi mdi-file-document me-2 font-size-18"></i>
                          <div class="flex-grow-1">
                            <h6 class="mb-1">{{ attachment.name }}</h6>
                            <small class="text-muted">{{ formatFileSize(attachment.size) }} - Uploaded {{ formatDateTime(attachment.uploadedAt) }}</small>
                          </div>
                          <a :href="attachment.path" class="btn btn-sm btn-primary" download>
                            <i class="mdi mdi-download me-1"></i> Download
                          </a>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center p-4">
                      <i class="mdi mdi-file-document-outline font-size-48 text-muted"></i>
                      <p class="mt-2">No attachments available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Comments Section -->
            <div class="card mb-4">
              <div class="card-header bg-transparent d-flex justify-content-between align-items-center">
                <h5 class="card-title mb-0">Comments</h5>
                <span class="badge bg-primary">{{ selectedAnnouncement.comments ? selectedAnnouncement.comments.length : 0 }}</span>
              </div>
              <div class="card-body">
                <div v-if="selectedAnnouncement.comments && selectedAnnouncement.comments.length > 0" class="comments-section">
                  <div v-for="comment in selectedAnnouncement.comments" :key="comment.id" class="comment-item mb-3 p-3 border rounded">
                    <div class="d-flex justify-content-between mb-2">
                      <div class="d-flex align-items-center">
                        <div class="avatar-xs me-2 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center">
                          {{ getInitials(comment.employee_name) }}
                        </div>
                        <span class="fw-medium">{{ comment.employee_name }}</span>
                      </div>
                      <div class="comment-actions">
                        <small class="text-muted me-2">{{ formatDateTime(comment.created_at) }}</small>
                        <button
                            v-if="canDeleteComment(comment)"
                            class="btn btn-sm btn-link text-danger p-0"
                            @click="deleteComment(comment.id)"
                        >
                          <i class="mdi mdi-delete-outline"></i>
                        </button>
                      </div>
                    </div>
                    <div class="comment-content">
                      {{ comment.comment }}
                    </div>
                  </div>
                </div>
                <div v-else class="text-center p-4">
                  <i class="mdi mdi-comment-outline font-size-48 text-muted"></i>
                  <p class="mt-2">No comments yet</p>
                </div>

                <!-- Add Comment Form -->
                <div v-if="selectedAnnouncement.status === 'Active'" class="add-comment-form mt-4">
                  <h6 class="font-size-14 mb-3">Add a Comment</h6>
                  <div class="d-flex">
                    <textarea v-model="commentForm.comment" class="form-control me-2" rows="2" placeholder="Write your comment here..."></textarea>
                    <button
                        class="btn btn-primary align-self-end"
                        @click="addComment"
                        :disabled="!commentForm.comment.trim()"
                    >
                      Post
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Activity Timeline -->
            <div v-if="selectedAnnouncement.activities && selectedAnnouncement.activities.length > 0" class="card mb-4">
              <div class="card-header bg-transparent">
                <h5 class="card-title mb-0">Activity History</h5>
              </div>
              <div class="card-body">
                <div class="activity-timeline position-relative">
                  <div v-for="(activity, index) in selectedAnnouncement.activities" :key="index" class="activity-item position-relative ps-5 pb-4">
                    <div class="activity-point position-absolute" style="left: 0; top: 0;">
                      <div :class="['activity-icon rounded-circle d-flex align-items-center justify-content-center', getActivityTypeClass(activity.activity_type)]" style="width: 36px; height: 36px;">
                        <i :class="['mdi', getActivityTypeIcon(activity.activity_type)]"></i>
                      </div>
                    </div>
                    <div class="activity-content">
                      <div class="d-flex justify-content-between mb-1">
                        <h6 class="font-size-14 mb-0">{{ activity.title }}</h6>
                        <small class="text-muted">{{ formatDateTime(activity.timestamp) }}</small>
                      </div>
                      <p class="text-muted mb-0">{{ activity.description }}</p>
                      <small class="text-muted">By: {{ activity.user }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Acknowledgment Section -->
            <div v-if="selectedAnnouncement.require_acknowledgment" class="card mb-4">
              <div class="card-header bg-transparent d-flex justify-content-between align-items-center">
                <h5 class="card-title mb-0">Acknowledgments</h5>
                <span class="badge bg-success">{{ selectedAnnouncement.acknowledgments ? selectedAnnouncement.acknowledgments.length : 0 }}</span>
              </div>
              <div class="card-body">
                <div v-if="selectedAnnouncement.acknowledgments && selectedAnnouncement.acknowledgments.length > 0" class="table-responsive">
                  <table class="table table-bordered table-striped">
                    <thead class="table-light">
                    <tr>
                      <th>Employee Name</th>
                      <th>Acknowledged At</th>
                      <th>Feedback</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="ack in selectedAnnouncement.acknowledgments" :key="ack.id">
                      <td>{{ ack.employee_name }}</td>
                      <td>{{ formatDateTime(ack.acknowledged_at) }}</td>
                      <td>{{ ack.feedback || 'No feedback provided' }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="text-center p-4">
                  <i class="mdi mdi-check-circle-outline font-size-48 text-muted"></i>
                  <p class="mt-2">No acknowledgments received yet</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button v-if="selectedAnnouncement && !hasAcknowledged && selectedAnnouncement.require_acknowledgment && selectedAnnouncement.status === 'Active'" type="button" class="btn btn-success" @click="acknowledgeAnnouncement">
            <i class="mdi mdi-check me-1"></i> Acknowledge
          </button>
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button v-if="selectedAnnouncement && canEditAnnouncement(selectedAnnouncement)" type="button" class="btn btn-primary" @click="editAnnouncement(selectedAnnouncement)">
            <i class="mdi mdi-pencil me-1"></i> Edit
          </button>
        </div>
      </div>
    </div>
  </div>



  <!-- Announcement Create/Edit Modal -->
  <div class="modal fade" id="announcementModal" tabindex="-1" aria-labelledby="announcementModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="announcementModalLabel">{{ isEditMode ? 'Edit Announcement' : 'Create New Announcement' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form id="announcementForm" @submit.prevent="submitAnnouncement">
            <div class="row g-3">
              <div class="col-md-12">
                <label class="form-label">Title <span class="text-danger">*</span></label>
                <input v-model="announcementForm.title" type="text" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Announcement Type <span class="text-danger">*</span></label>
                <select v-model="announcementForm.announcementType" class="form-select" required>
                  <option value="" disabled>Select Announcement Type</option>
                  <option v-for="type in announcementTypes" :key="type" :value="type">{{ type }}</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Priority <span class="text-danger">*</span></label>
                <select v-model="announcementForm.priority" class="form-select" required>
                  <option value="" disabled>Select Priority</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Publish Date <span class="text-danger">*</span></label>
                <input v-model="announcementForm.publishDate" type="date" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Expiry Date</label>
                <input v-model="announcementForm.expiryDate" type="date" class="form-control">
                <small class="text-muted">Leave blank if the announcement doesn't expire</small>
              </div>

              <div class="col-md-12">
                <label class="form-label">Target Audience <span class="text-danger">*</span></label>
                <div class="d-flex flex-column gap-2">
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" v-model="announcementForm.targetAudience" id="targetAll" value="All Employees">
                    <label class="form-check-label" for="targetAll">All Employees</label>
                  </div>
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" v-model="announcementForm.targetAudience" id="targetDepartments" value="Specific Departments">
                    <label class="form-check-label" for="targetDepartments">Specific Departments</label>
                  </div>
                </div>
              </div>

              <div v-if="announcementForm.targetAudience === 'Specific Departments'" class="col-md-12">
                <label class="form-label">Select Departments <span class="text-danger">*</span></label>
                <div class="card p-3">
                  <div class="department-selection">
                    <div v-for="dept in departments" :key="dept.id" class="form-check mb-2">
                      <input
                          :id="`dept-${dept.id}`"
                          type="checkbox"
                          :value="dept.id"
                          v-model="announcementForm.departments"
                          class="form-check-input"
                      >
                      <label :for="`dept-${dept.id}`" class="form-check-label">{{ dept.departmentName }}</label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-12">
                <label class="form-label">Content <span class="text-danger">*</span></label>
                <div class="rich-text-editor border rounded p-3" style="min-height: 300px;">
                  <textarea v-model="announcementForm.content" class="form-control" rows="10" required></textarea>
                </div>
                <small class="text-muted">
                  Create your announcement content here. You can use simple formatting with plain text.
                </small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Status <span class="text-danger">*</span></label>
                <select v-model="announcementForm.status" class="form-select" required>
                  <option value="" disabled>Select Status</option>
                  <option value="Active">Publish Immediately</option>
                  <option value="Draft">Save as Draft</option>
                </select>
              </div>

              <div class="col-md-6">
                <div class="form-check form-switch mt-4">
                  <input
                      v-model="announcementForm.requireAcknowledgment"
                      class="form-check-input"
                      type="checkbox"
                      id="requireAcknowledgmentSwitch"
                  >
                  <label class="form-check-label" for="requireAcknowledgmentSwitch">
                    Require employee acknowledgment
                  </label>
                </div>
              </div>

              <div class="col-md-12">
                <label class="form-label">Attachments</label>
                <input
                    type="file"
                    class="form-control"
                    multiple
                    ref="attachmentFiles"
                    @change="handleFileSelection"
                >
                <small class="text-muted">Attach relevant documents, images, or files</small>
              </div>

              <!-- Display existing attachments when editing -->
              <div v-if="announcementForm.attachmentsList && announcementForm.attachmentsList.length > 0" class="col-md-12">
                <label class="form-label">Existing Attachments</label>
                <div class="list-group">
                  <div v-for="(attachment, index) in announcementForm.attachmentsList" :key="index" class="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                    <div>
                      <i class="mdi mdi-file-document me-2"></i>
                      {{ attachment.name }}
                    </div>
                    <button
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removeAttachment(attachment, index)"
                    >
                      <i class="mdi mdi-delete"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="submit" form="announcementForm" class="btn btn-primary" :disabled="submitLoading">
            <span v-if="submitLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            {{ isEditMode ? 'Update Announcement' : 'Publish Announcement' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Acknowledgment Modal -->
  <div class="modal fade" id="acknowledgeModal" tabindex="-1" aria-labelledby="acknowledgeModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="acknowledgeModalLabel">Acknowledge Announcement</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedAnnouncement">
            <p>You are acknowledging the following announcement:</p>
            <div class="alert alert-info">
              <h5 class="alert-heading">{{ selectedAnnouncement.title }}</h5>
              <p class="mb-0"><strong>Type:</strong> {{ selectedAnnouncement.announcement_type }}</p>
              <p class="mb-0"><strong>Published:</strong> {{ formatDate(selectedAnnouncement.publish_date) }}</p>
            </div>
            <div class="mb-3">
              <label class="form-label">Feedback (Optional)</label>
              <textarea v-model="acknowledgeForm.feedback" class="form-control" rows="4" placeholder="You may provide optional feedback about this announcement..."></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-success" @click="confirmAcknowledgeAnnouncement">
            <i class="mdi mdi-check me-1"></i> Acknowledge Announcement
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Acknowledgment Stats Modal -->
  <div class="modal fade" id="acknowledgeStatsModal" tabindex="-1" aria-labelledby="acknowledgeStatsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="acknowledgeStatsModalLabel">Acknowledgment Statistics</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedAnnouncement">
            <h5 class="mb-3">{{ selectedAnnouncement.title }}</h5>

            <!-- Overall Stats -->
            <div class="row mb-4">
              <div class="col-md-6 mb-3">
                <div class="card">
                  <div class="card-body">
                    <h6 class="card-title mb-3">Acknowledgment Progress</h6>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Acknowledged</span>
                      <span class="text-success">{{ ackStats.acknowledgedCount }} / {{ ackStats.totalEmployees }}</span>
                    </div>
                    <div class="progress mb-3" style="height: 10px;">
                      <div class="progress-bar bg-success" role="progressbar" :style="`width: ${ackStats.acknowledgedPercentage}%`" :aria-valuenow="ackStats.acknowledgedPercentage" aria-valuemin="0" aria-valuemax="100">
                        {{ ackStats.acknowledgedPercentage }}%
                      </div>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Pending</span>
                      <span class="text-warning">{{ ackStats.pendingCount }} / {{ ackStats.totalEmployees }}</span>
                    </div>
                    <div class="progress" style="height: 10px;">
                      <div class="progress-bar bg-warning" role="progressbar" :style="`width: ${ackStats.pendingPercentage}%`" :aria-valuenow="ackStats.pendingPercentage" aria-valuemin="0" aria-valuemax="100">
                        {{ ackStats.pendingPercentage }}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="card">
                  <div class="card-body">
                    <h6 class="card-title mb-3">Summary</h6>
                    <ul class="list-group list-group-flush">
                      <li class="list-group-item d-flex justify-content-between align-items-center px-0">
                        Total Employees
                        <span class="badge bg-primary rounded-pill">{{ ackStats.totalEmployees }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center px-0">
                        Acknowledged
                        <span class="badge bg-success rounded-pill">{{ ackStats.acknowledgedCount }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center px-0">
                        Pending
                        <span class="badge bg-warning rounded-pill">{{ ackStats.pendingCount }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Department Stats -->
            <div v-if="ackStats.departmentStats && ackStats.departmentStats.length > 0" class="card mb-4">
              <div class="card-header bg-transparent">
                <h6 class="card-title mb-0">Department Breakdown</h6>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead class="table-light">
                    <tr>
                      <th>Department</th>
                      <th class="text-center">Employees</th>
                      <th class="text-center">Acknowledged</th>
                      <th class="text-center">Pending</th>
                      <th class="text-center">Progress</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="dept in ackStats.departmentStats" :key="dept.id">
                      <td>{{ dept.name }}</td>
                      <td class="text-center">{{ dept.totalEmployees }}</td>
                      <td class="text-center">{{ dept.acknowledged }}</td>
                      <td class="text-center">{{ dept.pending }}</td>
                      <td>
                        <div class="progress" style="height: 8px;">
                          <div class="progress-bar bg-success" role="progressbar" :style="`width: ${dept.percentage}%`" :aria-valuenow="dept.percentage" aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                        <small class="d-block text-center mt-1">{{ dept.percentage }}%</small>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Recent Acknowledgments -->
            <div v-if="ackStats.recentAcknowledgments && ackStats.recentAcknowledgments.length > 0" class="card">
              <div class="card-header bg-transparent">
                <h6 class="card-title mb-0">Recent Acknowledgments</h6>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead class="table-light">
                    <tr>
                      <th>Employee</th>
                      <th>Acknowledged At</th>
                      <th>Feedback</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="ack in ackStats.recentAcknowledgments" :key="ack.id">
                      <td>{{ ack.employee_name }}</td>
                      <td>{{ formatDateTime(ack.acknowledged_at) }}</td>
                      <td>{{ ack.feedback || 'No feedback provided' }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-primary" @click="exportAcknowledgmentStats">
            <i class="mdi mdi-file-export me-1"></i> Export Statistics
          </button>
        </div>
      </div>
    </div>
  </div>


</template>

<script>
/* eslint-disable */
import { toast, authUrl, execute } from "@/api";
import _ from 'lodash';
import NavigationBar from "@/components/Navigation.vue";
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';

export default {
  name: "Announcements",
  components: {NavigationBar},
  data() {
    return {
      submitLoading: false,
      loading: false,
      announcements: [],
      recentAnnouncements: [],
      departments: [],
      announcementTypes: [
        'General',
        'Policy Update',
        'Event',
        'Emergency',
        'HR Announcement',
        'IT Notice',
        'Training',
        'Holiday',
        'Maintenance',
        'Other'
      ],
      filters: {
        departmentId: "",
        type: "",
        priority: "",
        status: "",
        startDate: this.getDateOffset(-90), // Last 90 days
        endDate: this.getDateOffset(365), // Next year
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalAnnouncements: 0,
        limit: 10
      },
      metrics: {
        totalAnnouncements: 0,
        highPriority: 0,
        mediumPriority: 0,
        lowPriority: 0,
        activeAnnouncements: 0,
        draftAnnouncements: 0,
        archivedAnnouncements: 0,
        requireAcknowledgment: 0,
        publishedToday: 0,
        expiringThisWeek: 0,
        typeCounts: {}
      },
      selectedAnnouncement: null,
      announcementForm: {
        title: "",
        announcementType: "General",
        content: "",
        publishDate: new Date().toISOString().split('T')[0],
        expiryDate: "",
        priority: "Medium",
        status: "Active",
        targetAudience: "All Employees",
        departments: [],
        requireAcknowledgment: false,
        attachmentsList: []
      },
      isEditMode: false,
      removedAttachments: [],
      pendingFiles: [],
      acknowledgeForm: {
        feedback: ""
      },
      commentForm: {
        comment: ""
      },
      hasAcknowledged: false,
      ackStats: {
        totalEmployees: 0,
        acknowledgedCount: 0,
        pendingCount: 0,
        acknowledgedPercentage: 0,
        pendingPercentage: 0,
        departmentStats: [],
        recentAcknowledgments: []
      }
    };
  },
  mounted() {
    this.getDepartments();
    this.getAnnouncements();
    this.getAnnouncementTypes();
  },
  methods: {
    getDateOffset(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getAnnouncementTypes() {
      const data = new FormData();
      data.append("function", "getAnnouncementTypes");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.announcementTypes = res.data.data.announcementTypes || this.announcementTypes;
            } else {
              console.error("Error fetching announcement types:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    getAnnouncements() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getAnnouncements");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.filters.page);
      data.append("limit", this.filters.limit);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.type) {
        data.append("type", this.filters.type);
      }

      if (this.filters.priority) {
        data.append("priority", this.filters.priority);
      }

      if (this.filters.status) {
        data.append("status", this.filters.status);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.announcements = res.data.data.announcements || [];
              this.metrics = res.data.data.metrics || {};
              this.recentAnnouncements = res.data.data.recentAnnouncements || [];
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalAnnouncements: 0,
                limit: 10
              };
            } else {
              console.error("Error fetching announcements:", res.data.message);
              this.showToast("error", "Failed to load announcements data: " + res.data.message);
              this.announcements = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching announcements");
            this.announcements = [];
            this.metrics = {};
          });
    },

    refreshData() {
      this.getAnnouncements();
    },

    resetFilters() {
      this.filters = {
        departmentId: "",
        type: "",
        priority: "",
        status: "",
        startDate: this.getDateOffset(-90),
        endDate: this.getDateOffset(365),
        search: "",
        page: 1,
        limit: 10
      };
      this.getAnnouncements();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getAnnouncements();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    createAnnouncement() {
      this.isEditMode = false;
      this.announcementForm = {
        title: "",
        announcementType: "General",
        content: "",
        publishDate: new Date().toISOString().split('T')[0],
        expiryDate: "",
        priority: "Medium",
        status: "Active",
        targetAudience: "All Employees",
        departments: [],
        requireAcknowledgment: false,
        attachmentsList: []
      };

      this.removedAttachments = [];
      this.pendingFiles = [];

      new bootstrap.Modal(document.getElementById('announcementModal')).show();
    },

    editAnnouncement(announcement) {
      this.isEditMode = true;
      this.selectedAnnouncement = announcement;

      // Close the details modal if it's open
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('announcementDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Prepare form data
      this.announcementForm = {
        announcementId: announcement.id,
        title: announcement.title,
        announcementType: announcement.announcement_type,
        content: announcement.content,
        publishDate: announcement.publish_date,
        expiryDate: announcement.expiry_date || "",
        priority: announcement.priority,
        status: announcement.status,
        targetAudience: announcement.target_audience,
        departments: announcement.departmentsList || [],
        requireAcknowledgment: Boolean(announcement.require_acknowledgment),
        attachmentsList: announcement.attachmentsList || []
      };

      this.removedAttachments = [];
      this.pendingFiles = [];

      // Open the modal after a brief timeout to ensure the details modal has closed
      setTimeout(() => {
        new bootstrap.Modal(document.getElementById('announcementModal')).show();
      }, 500);
    },

    handleFileSelection(event) {
      this.pendingFiles = event.target.files;
    },

    removeAttachment(attachment, index) {
      this.removedAttachments.push(attachment.path);
      this.announcementForm.attachmentsList.splice(index, 1);
    },


// Improved submitAnnouncement method with comprehensive error handling and debugging
    submitAnnouncement() {
      console.log("submitAnnouncement method called");

      // Validate form
      if (!this.validateAnnouncementForm()) {
        console.log("Form validation failed");
        return;
      }

      console.log("Form validation successful, preparing to submit");
      this.submitLoading = true;
      const data = new FormData();

      try {
        if (this.isEditMode) {
          data.append("function", "updateAnnouncement");
          data.append("announcementId", this.announcementForm.announcementId);
          console.log("Update mode - editing announcement ID:", this.announcementForm.announcementId);
        } else {
          data.append("function", "createAnnouncement");
          console.log("Create mode - creating new announcement");
        }

        // Add all form fields
        data.append("title", this.announcementForm.title);
        data.append("announcementType", this.announcementForm.announcementType);
        data.append("content", this.announcementForm.content);
        data.append("publishDate", this.announcementForm.publishDate);
        data.append("priority", this.announcementForm.priority);
        data.append("status", this.announcementForm.status);
        data.append("targetAudience", this.announcementForm.targetAudience);
        data.append("requireAcknowledgment", this.announcementForm.requireAcknowledgment ? 1 : 0);

        if (this.announcementForm.expiryDate) {
          data.append("expiryDate", this.announcementForm.expiryDate);
        }

        // Handle departments
        if (this.announcementForm.targetAudience === 'Specific Departments' && this.announcementForm.departments.length > 0) {
          data.append("departments", JSON.stringify(this.announcementForm.departments));
          console.log("Added departments:", this.announcementForm.departments);
        }

        // Handle file uploads
        if (this.pendingFiles && this.pendingFiles.length > 0) {
          console.log("Adding", this.pendingFiles.length, "file attachments");
          for (let i = 0; i < this.pendingFiles.length; i++) {
            data.append(`attachment_${i}`, this.pendingFiles[i]);
          }
        }

        // Handle attachment removals
        if (this.isEditMode && this.removedAttachments.length > 0) {
          data.append("removedAttachments", JSON.stringify(this.removedAttachments));
          console.log("Removed attachments:", this.removedAttachments);
        }

        console.log("Form data prepared, attempting to execute API call");

        // Check if execute function exists
        if (typeof execute !== 'function') {
          throw new Error("Execute function is not available!");
        }

        // Log the form data for debugging
        console.log("FormData contents:");
        for (let pair of data.entries()) {
          console.log(pair[0] + ': ' + (pair[0].includes('attachment') ? '[File]' : pair[1]));
        }

        // Submit the form
        execute(data, authUrl)
            .then((res) => {
              console.log("API response received:", res.data);
              this.submitLoading = false;

              if (res.data.success) {
                // Close the modal
                const modal = bootstrap.Modal.getInstance(document.getElementById('announcementModal'));
                if (modal) {
                  console.log("Closing modal");
                  modal.hide();
                } else {
                  console.warn("Modal instance not found, trying to close manually");
                  // Fallback if bootstrap modal instance isn't found
                  try {
                    const modalEl = document.getElementById('announcementModal');
                    const bsModal = new bootstrap.Modal(modalEl);
                    bsModal.hide();
                  } catch (e) {
                    console.error("Failed to close modal:", e);
                  }
                }

                this.showToast("success", res.data.message || "Announcement saved successfully");
                this.getAnnouncements(); // Refresh the data

                // Reset form data
                this.resetAnnouncementForm();

                // If we just created a new active announcement, show a success message
                if (!this.isEditMode && this.announcementForm.status === 'Active') {
                  this.showToast("info", "Announcement has been published successfully!");
                }
              } else {
                console.error("API returned error:", res.data.message);
                this.showToast("error", res.data.message || "Failed to save announcement");
              }
            })
            .catch((error) => {
              console.error("API call failed:", error);
              this.submitLoading = false;
              this.showToast("error", "Network error when saving announcement: " + (error.message || "Unknown error"));
            });
      } catch (error) {
        console.error("Error in form submission process:", error);
        this.submitLoading = false;
        this.showToast("error", "Error preparing form data: " + error.message);
      }
    },

// Helper method to reset the form
    resetAnnouncementForm() {
      this.announcementForm = {
        title: "",
        announcementType: "General",
        content: "",
        publishDate: new Date().toISOString().split('T')[0],
        expiryDate: "",
        priority: "Medium",
        status: "Active",
        targetAudience: "All Employees",
        departments: [],
        requireAcknowledgment: false,
        attachmentsList: []
      };
      this.pendingFiles = [];
      this.removedAttachments = [];

      // Reset file input if it exists
      if (this.$refs.attachmentFiles) {
        this.$refs.attachmentFiles.value = '';
      }
    },

// Enhanced validation method
    validateAnnouncementForm() {
      const errors = [];

      // Title validation
      if (!this.announcementForm.title.trim()) {
        errors.push("Title is required");
      }

      // Content validation
      if (!this.announcementForm.content.trim()) {
        errors.push("Content is required");
      }

      // Type validation
      if (!this.announcementForm.announcementType) {
        errors.push("Announcement type is required");
      }

      // Date validation
      if (!this.announcementForm.publishDate) {
        errors.push("Publish date is required");
      }

      // Expiry date check
      if (this.announcementForm.expiryDate &&
          new Date(this.announcementForm.expiryDate) <= new Date(this.announcementForm.publishDate)) {
        errors.push("Expiry date must be after publish date");
      }

      // Department validation
      if (this.announcementForm.targetAudience === 'Specific Departments' &&
          (!this.announcementForm.departments || this.announcementForm.departments.length === 0)) {
        errors.push("Please select at least one department");
      }

      // If there are errors, show them and return false
      if (errors.length > 0) {
        console.error("Form validation errors:", errors);
        this.showToast("error", errors[0]); // Show the first error
        return false;
      }

      // All validations passed
      return true;
    },


    viewAnnouncementDetails(announcement) {
      this.getAnnouncementDetails(announcement.id);
    },

    viewAcknowledgmentStats(announcement) {
      this.selectedAnnouncement = announcement;
      this.getAcknowledgmentStats(announcement.id);
    },

    acknowledgeAnnouncement() {
      this.acknowledgeForm = {
        feedback: ""
      };

      // Close the details modal
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('announcementDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Show acknowledgment modal
      setTimeout(() => {
        new bootstrap.Modal(document.getElementById('acknowledgeModal')).show();
      }, 500);
    },

    archiveAnnouncement(announcement) {
      if (confirm(`Are you sure you want to archive the announcement "${announcement.title}"?`)) {
        const data = new FormData();
        data.append("function", "archiveAnnouncement");
        data.append("announcementId", announcement.id);

        execute(data, authUrl)
            .then((res) => {
              if (res.data.success) {
                this.showToast("success", res.data.message);
                this.getAnnouncements(); // Refresh the data
              } else {
                this.showToast("error", res.data.message);
              }
            })
            .catch((error) => {
              console.error("API error:", error);
              this.showToast("error", "Network error when archiving announcement");
            });
      }
    },

    getAnnouncementDetails(announcementId) {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getAnnouncementDetails");
      data.append("announcementId", announcementId);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.selectedAnnouncement = res.data.data.announcement;

              // Open the modal
              new bootstrap.Modal(document.getElementById('announcementDetailsModal')).show();

              // Check if the current user has acknowledged this announcement
              this.hasAcknowledged = false;
              if (this.selectedAnnouncement.acknowledgments) {
                // Get current user's ID
                const token = localStorage.getItem('token');
                const payload = JSON.parse(atob(token.split('.')[1]));
                const userId = payload.username;

                this.hasAcknowledged = this.selectedAnnouncement.acknowledgments.some(ack =>
                    ack.employee_id === userId
                );
              }

              // Reset comment form
              this.commentForm.comment = "";

          /*    // Open the modal
              new bootstrap.Modal(document.getElementById('announcementDetailsModal')).show();
*/

            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching announcement details");
          });
    },

    confirmAcknowledgeAnnouncement() {
      const data = new FormData();
      data.append("function", "acknowledgeAnnouncement");
      data.append("announcementId", this.selectedAnnouncement.id);
      data.append("feedback", this.acknowledgeForm.feedback);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Close the acknowledgment modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('acknowledgeModal'));
              if (modal) {
                modal.hide();
              }

              this.showToast("success", res.data.message);

              // Update the announcement details
              this.getAnnouncementDetails(this.selectedAnnouncement.id);
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when acknowledging announcement");
          });
    },

    addComment() {
      if (!this.commentForm.comment.trim()) {
        this.showToast("error", "Comment cannot be empty");
        return;
      }

      const data = new FormData();
      data.append("function", "addComment");
      data.append("announcementId", this.selectedAnnouncement.id);
      data.append("comment", this.commentForm.comment);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", res.data.message);

              // Add the new comment to the list
              if (!this.selectedAnnouncement.comments) {
                this.selectedAnnouncement.comments = [];
              }

              this.selectedAnnouncement.comments.push({
                id: res.data.data.commentId,
                announcement_id: this.selectedAnnouncement.id,
                employee_id: "current_user", // This will be replaced when we refresh
                employee_name: res.data.data.employeeName,
                comment: this.commentForm.comment,
                created_at: res.data.data.created_at,
                status: 'Active'
              });

              // Clear the comment form
              this.commentForm.comment = "";
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when adding comment");
          });
    },

    deleteComment(commentId) {
      if (confirm("Are you sure you want to delete this comment?")) {
        const data = new FormData();
        data.append("function", "deleteComment");
        data.append("commentId", commentId);

        execute(data, authUrl)
            .then((res) => {
              if (res.data.success) {
                this.showToast("success", res.data.message);

                // Update the comment list by marking it as deleted
                const commentIndex = this.selectedAnnouncement.comments.findIndex(c => c.id === commentId);
                if (commentIndex !== -1) {
                  this.selectedAnnouncement.comments.splice(commentIndex, 1);
                }
              } else {
                this.showToast("error", res.data.message);
              }
            })
            .catch((error) => {
              console.error("API error:", error);
              this.showToast("error", "Network error when deleting comment");
            });
      }
    },

    getAcknowledgmentStats(announcementId) {
      const data = new FormData();
      data.append("function", "getAcknowledgmentStats");
      data.append("announcementId", announcementId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.ackStats = res.data.data;

              // Open the stats modal
              new bootstrap.Modal(document.getElementById('acknowledgeStatsModal')).show();
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching acknowledgment statistics");
          });
    },

    exportAnnouncements() {
      try {
        // First, determine if we should export filtered data or all data
        this.loading = true;

        // Get all announcements if we need to export everything
        // This is only necessary if the current view is filtered
        const isFiltered = this.filters.departmentId || this.filters.type ||
            this.filters.priority || this.filters.status || this.filters.search;

        if (isFiltered) {
          // Create a copy of current filters but remove pagination
          const exportFilters = {...this.filters, page: 1, limit: 1000};

          const data = new FormData();
          data.append("function", "getAnnouncements");
          data.append("startDate", exportFilters.startDate);
          data.append("endDate", exportFilters.endDate);
          data.append("page", exportFilters.page);
          data.append("limit", exportFilters.limit);

          if (exportFilters.departmentId) {
            data.append("departmentId", exportFilters.departmentId);
          }

          if (exportFilters.type) {
            data.append("type", exportFilters.type);
          }

          if (exportFilters.priority) {
            data.append("priority", exportFilters.priority);
          }

          if (exportFilters.status) {
            data.append("status", exportFilters.status);
          }

          if (exportFilters.search) {
            data.append("search", exportFilters.search);
          }

          // Fetch all data for export
          execute(data, authUrl)
              .then((res) => {
                this.loading = false;
                if (res.data.success) {
                  const announcementsToExport = res.data.data.announcements || [];
                  this.generateCSV(announcementsToExport);
                } else {
                  this.showToast("error", "Failed to fetch data for export: " + res.data.message);
                }
              })
              .catch((error) => {
                this.loading = false;
                console.error("API error:", error);
                this.showToast("error", "Network error when fetching data for export");
              });
        } else {
          // Use the announcements we already have if there are no filters
          this.generateCSV(this.announcements);
          this.loading = false;
        }
      } catch (error) {
        this.loading = false;
        console.error("Export error:", error);
        this.showToast("error", "Error exporting announcements: " + error.message);
      }
    },

    exportAcknowledgmentStats() {
      if (!this.selectedAnnouncement || !this.ackStats) {
        this.showToast("error", "No acknowledgment statistics to export");
        return;
      }

      try {
        // Create new PDF document
        const doc = new jsPDF();

        // Add title
        doc.setFontSize(20);
        doc.setTextColor(40, 40, 40);
        doc.text("Acknowledgment Statistics Report", 105, 20, { align: "center" });

        // Add announcement info
        doc.setFontSize(16);
        doc.text("Announcement Information", 14, 35);
        doc.setDrawColor(220, 220, 220);
        doc.line(14, 37, 196, 37);

        doc.setFontSize(12);
        doc.text(`Title: ${this.selectedAnnouncement.title}`, 14, 45);
        doc.text(`Type: ${this.selectedAnnouncement.announcement_type}`, 14, 52);
        doc.text(`Published: ${this.formatDate(this.selectedAnnouncement.publish_date)}`, 14, 59);
        doc.text(`Status: ${this.selectedAnnouncement.status}`, 14, 66);

        // Add overall stats
        doc.setFontSize(16);
        doc.text("Overall Statistics", 14, 80);
        doc.setDrawColor(220, 220, 220);
        doc.line(14, 82, 196, 82);

        // Create table for overall stats
        autoTable(doc, {
          startY: 90,
          head: [['Metric', 'Count', 'Percentage']],
          body: [
            ['Total Employees', this.ackStats.totalEmployees, '100%'],
            ['Acknowledged', this.ackStats.acknowledgedCount, `${this.ackStats.acknowledgedPercentage}%`],
            ['Pending', this.ackStats.pendingCount, `${this.ackStats.pendingPercentage}%`]
          ],
          theme: 'striped',
          headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
          styles: { overflow: 'linebreak', cellPadding: 5 }
        });

        // Add department stats if available
        if (this.ackStats.departmentStats && this.ackStats.departmentStats.length > 0) {
          doc.setFontSize(16);
          doc.text("Department Breakdown", 14, doc.lastAutoTable.finalY + 20);
          doc.setDrawColor(220, 220, 220);
          doc.line(14, doc.lastAutoTable.finalY + 22, 196, doc.lastAutoTable.finalY + 22);

          const deptRows = this.ackStats.departmentStats.map(dept => [
            dept.name,
            dept.totalEmployees,
            dept.acknowledged,
            dept.pending,
            `${dept.percentage}%`
          ]);

          autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 30,
            head: [['Department', 'Total', 'Acknowledged', 'Pending', 'Progress']],
            body: deptRows,
            theme: 'striped',
            headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
            styles: { overflow: 'linebreak', cellPadding: 5 }
          });
        }

        // Add recent acknowledgments if available
        if (this.ackStats.recentAcknowledgments && this.ackStats.recentAcknowledgments.length > 0) {
          // Add a new page if we're getting close to the bottom
          if (doc.lastAutoTable.finalY > 200) {
            doc.addPage();
            doc.setFontSize(16);
            doc.text("Recent Acknowledgments", 14, 20);
            doc.setDrawColor(220, 220, 220);
            doc.line(14, 22, 196, 22);

            const ackRows = this.ackStats.recentAcknowledgments.map(ack => [
              ack.employee_name,
              this.formatDateTime(ack.acknowledged_at),
              ack.feedback || 'No feedback provided'
            ]);

            autoTable(doc, {
              startY: 30,
              head: [['Employee', 'Acknowledged At', 'Feedback']],
              body: ackRows,
              theme: 'striped',
              headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
              styles: { overflow: 'linebreak', cellPadding: 5 },
              columnStyles: {
                0: { cellWidth: 50 },
                1: { cellWidth: 50 },
                2: { cellWidth: 90 }
              }
            });
          } else {
            doc.setFontSize(16);
            doc.text("Recent Acknowledgments", 14, doc.lastAutoTable.finalY + 20);
            doc.setDrawColor(220, 220, 220);
            doc.line(14, doc.lastAutoTable.finalY + 22, 196, doc.lastAutoTable.finalY + 22);

            const ackRows = this.ackStats.recentAcknowledgments.map(ack => [
              ack.employee_name,
              this.formatDateTime(ack.acknowledged_at),
              ack.feedback || 'No feedback provided'
            ]);

            autoTable(doc, {
              startY: doc.lastAutoTable.finalY + 30,
              head: [['Employee', 'Acknowledged At', 'Feedback']],
              body: ackRows,
              theme: 'striped',
              headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
              styles: { overflow: 'linebreak', cellPadding: 5 },
              columnStyles: {
                0: { cellWidth: 50 },
                1: { cellWidth: 50 },
                2: { cellWidth: 90 }
              }
            });
          }
        }

        // Add footer with page numbers
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);

          // Current date at bottom left
          doc.text(
              `Generated: ${new Date().toLocaleString()}`,
              14,
              doc.internal.pageSize.height - 10
          );

          // Page numbers at bottom right
          doc.text(
              `Page ${i} of ${pageCount}`,
              doc.internal.pageSize.width - 14,
              doc.internal.pageSize.height - 10,
              { align: 'right' }
          );
        }

        // Save the PDF file
        doc.save(`acknowledgment-stats-${this.selectedAnnouncement.id}.pdf`);
        this.showToast("success", "Acknowledgment statistics exported as PDF successfully");
      } catch (error) {
        console.error("PDF generation error:", error);
        this.showToast("error", "Error generating PDF: " + error.message);
      }
    },

    generateCSV(announcementsData) {
      if (!announcementsData || announcementsData.length === 0) {
        this.showToast("warning", "No announcements data to export");
        return;
      }

      // Define the CSV columns
      const csvColumns = [
        "ID",
        "Title",
        "Type",
        "Priority",
        "Status",
        "Publish Date",
        "Expiry Date",
        "Creator",
        "Created At",
        "Views",
        "Requires Acknowledgment",
        "Content"
      ];

      // Convert announcements data to CSV rows
      const csvRows = announcementsData.map(announcement => [
        announcement.id || "",
        announcement.title || "",
        announcement.announcement_type || "",
        announcement.priority || "",
        announcement.status || "",
        announcement.publish_date ? this.formatDate(announcement.publish_date) : "",
        announcement.expiry_date ? this.formatDate(announcement.expiry_date) : "",
        announcement.created_by || "",
        announcement.created_at ? this.formatDateTime(announcement.created_at) : "",
        announcement.views || "0",
        announcement.require_acknowledgment ? "Yes" : "No",
        announcement.content ? `"${announcement.content.replace(/"/g, '""')}"` : ""
      ]);

      // Create CSV content
      let csvContent = [csvColumns, ...csvRows]
          .map(row => row.join(","))
          .join("\n");

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      const filename = `announcements-${formattedDate}.csv`;

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.showToast("success", "Announcements data exported successfully");
    },

    // Permission checks
    canEditAnnouncement(announcement) {
      // You can add additional permission checks here if needed
      return announcement.status !== 'Archived';
    },

    canDeleteComment(comment) {
      try {
        // Get current user's ID
        const token = localStorage.getItem('token');

        // Check if token exists
        if (!token) {
          console.warn('No authentication token found');
          return false; // No token means no permission
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        const userId = payload.username;

        // Check if user is the comment author or an admin
        return comment.employee_id === userId || this.isUserAdmin();
      } catch (error) {
        console.error('Error checking comment deletion permission:', error);
        return false; // If there's any error, deny permission
      }
    },



    isUserAdmin() {
      try {
        // Get current user's roles
        const token = localStorage.getItem('token');

        // Check if token exists
        if (!token) {
          console.warn('No authentication token found');
          return false;
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        const roles = payload.roles || [];

        return roles.includes('admin') || roles.includes('hr');
      } catch (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
    },
    // Utility methods
    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';

      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short' });
    },

    formatDateDay(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.getDate();
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';

      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
    },

    formatAnnouncementContent(content) {
      if (!content) return '';

      // Convert URLs to clickable links
      const urlPattern = /(https?:\/\/[^\s]+)/g;
      let formattedContent = content.replace(urlPattern, url => `<a href="${url}" target="_blank">${url}</a>`);

      // Convert line breaks to <br> tags
      formattedContent = formattedContent.replace(/(?:\r\n|\r|\n)/g, '<br>');

      return formattedContent;
    },

    getAnnouncementTypeColor(type) {
      if (!type) return 'bg-secondary';

      const typeMap = {
        'General': 'bg-primary',
        'Policy Update': 'bg-info',
        'Event': 'bg-success',
        'Emergency': 'bg-danger',
        'HR Announcement': 'bg-warning',
        'IT Notice': 'bg-dark',
        'Training': 'bg-info',
        'Holiday': 'bg-success',
        'Maintenance': 'bg-warning',
        'Other': 'bg-secondary'
      };

      return typeMap[type] || 'bg-secondary';
    },

    getPriorityBadgeClass(priority) {
      if (!priority) return 'bg-secondary';

      const priorityMap = {
        'High': 'bg-danger',
        'Medium': 'bg-warning',
        'Low': 'bg-info'
      };

      return priorityMap[priority] || 'bg-secondary';
    },

    getStatusBadgeClass(status) {
      if (!status) return 'bg-secondary';

      const statusMap = {
        'Active': 'bg-success',
        'Draft': 'bg-warning',
        'Archived': 'bg-secondary'
      };

      return statusMap[status] || 'bg-secondary';
    },

    getAnnouncementTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalAnnouncements) return 0;

      const count = this.metrics.typeCounts[type] || 0;
      const total = Object.values(this.metrics.typeCounts).reduce((sum, count) => sum + count, 0);
      return ((count / total) * 100).toFixed(1);
    },

    getActivityTypeClass(type) {
      if (!type) return 'bg-secondary text-white';

      const typeMap = {
        'create': 'bg-primary text-white',
        'update': 'bg-info text-white',
        'acknowledge': 'bg-success text-white',
        'comment': 'bg-purple text-white',
        'comment_delete': 'bg-danger text-white',
        'archive': 'bg-warning text-white'
      };

      return typeMap[type] || 'bg-secondary text-white';
    },

    getActivityTypeIcon(type) {
      if (!type) return 'mdi-help-circle';

      const typeMap = {
        'create': 'mdi-plus-circle',
        'update': 'mdi-pencil',
        'acknowledge': 'mdi-check-circle',
        'comment': 'mdi-comment-text',
        'comment_delete': 'mdi-comment-remove',
        'archive': 'mdi-archive'
      };

      return typeMap[type] || 'mdi-help-circle';
    },

    getDepartmentName(departmentId) {
      const department = this.departments.find(d => d.id === departmentId);
      return department ? department.departmentName : departmentId;
    },

    getInitials(name) {
      if (!name) return '';

      return name
          .split(' ')
          .map(word => word.charAt(0))
          .join('')
          .toUpperCase()
          .substring(0, 2);
    },

    truncateText(text, maxLength) {
      if (!text) return '';

      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    showToast(type, message) {
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      } else if (type === 'warning') {
        toast.warning(message);
      } else {
        toast.info(message);
      }
    }
  },
  watch: {
    'filters.departmentId': function() {
      this.filters.page = 1;
      this.getAnnouncements();
    },
    'filters.type': function() {
      this.filters.page = 1;
      this.getAnnouncements();
    },
    'filters.priority': function() {
      this.filters.page = 1;
      this.getAnnouncements();
    },
    'filters.status': function() {
      this.filters.page = 1;
      this.getAnnouncements();
    },
    'filters.search': _.debounce(function() {
      this.filters.page = 1;
      this.getAnnouncements();
    }, 500),
    'filters.startDate': function() {
      this.getAnnouncements();
    },
    'filters.endDate': function() {
      this.getAnnouncements();
    },
    'announcementForm.targetAudience': function(newValue) {
      if (newValue !== 'Specific Departments') {
        this.announcementForm.departments = [];
      }
    }
  }
};
</script>

<style scoped>
/* Card hover effects */
.card {
  transition: transform 0.3s, box-shadow 0.3s;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* Custom table styles */
.table th {
  font-weight: 600;
  color: #495057;
}

.table td {
  vertical-align: middle;
}

.announcement-date-badge {
  min-width: 45px;
}

.date-month {
  text-transform: uppercase;
  font-size: 0.8rem;
  color: #6c757d;
}

/* Announcement Type Colors */
.bg-purple {
  background-color: #6f42c1;
  color: white;
}

/* Department selection scrollable area */
.department-selection {
  max-height: 200px;
  overflow-y: auto;
}

/* Comment styles */
.comment-item {
  position: relative;
  background-color: #f8f9fa;
}

.comment-item:hover {
  background-color: #f1f3f5;
}

.comment-actions {
  display: flex;
  align-items: center;
}

/* Activity timeline styles */
.activity-timeline .activity-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 36px;
  left: 18px;
  width: 2px;
  height: calc(100% - 36px);
  background-color: #dee2e6;
}

/* Announcement content formatting */
.announcement-content {
  line-height: 1.6;
}

.announcement-content a {
  color: #0d6efd;
  text-decoration: underline;
}
</style>